import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Message, MessageTranslation, TranslationStatus } from '@itsappy/shared';
import { api } from '@/lib/api';

/**
 * Whether this workspace translates chat, and into what.
 *
 * Two answers in one: `available` is the deployment's — is there an engine at
 * all — and `enabled` is the workspace's. Both have to be true before a
 * Translate control is worth drawing, and Administration needs to tell them
 * apart to explain a switch that is on but doing nothing.
 *
 * Not cached forever, unlike the assistant's: an owner can turn this on while
 * their colleagues have the app open, and the point of the feature is that
 * nobody has to be told to reload.
 */
export function useTranslationStatus() {
  return useQuery({
    queryKey: ['translation', 'status'],
    queryFn: () => api.get<TranslationStatus>('/translation/status'),
    staleTime: 60_000,
  });
}

interface TranslateResult {
  lang: string;
  body: string;
  sourceLang: string;
}

/**
 * Translate one message on request.
 *
 * The result is written straight into the cached transcript rather than held
 * in the component, so it survives a scroll out of view, a thread being
 * opened, and the row being remounted — pressing Translate twice on the same
 * message is a thing people do when the answer disappears.
 */
export function useTranslateMessage(channelId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, language }: { messageId: string; language?: string }) =>
      api.post<TranslateResult>(`/translation/messages/${messageId}`, { language }),
    onSuccess: (result, { messageId }) => {
      if (!channelId) return;
      const translation: MessageTranslation = { lang: result.lang, body: result.body };
      const merge = (message: Message): Message =>
        message.id === messageId
          ? {
              ...message,
              sourceLang: result.sourceLang || message.sourceLang,
              translations: [
                ...(message.translations ?? []).filter((item) => item.lang !== result.lang),
                translation,
              ],
            }
          : message;

      queryClient.setQueriesData<{ items: Message[] }>(
        { queryKey: ['messages', channelId] },
        (old) => (old ? { ...old, items: old.items.map(merge) } : old),
      );
      queryClient.setQueriesData<{ items: Message[] }>({ queryKey: ['thread'] }, (old) =>
        old ? { ...old, items: old.items.map(merge) } : old,
      );
    },
  });
}
